import C15_SocialSharing from "@components/C15_SocialSharing/C15_SocialSharing";
import C200_1_BrandIconTitle from "@components/C200_1_BrandIconTitle/C200_1_BrandIconTitle";
import C30_SaveToMyProfile from "@components/C30_SaveToMyProfile/C30_SaveToMyProfile";
import { getTopicIdAndName } from "@components/CoveoSearchComponent/coveoUtils";
import Grid from "@components/Grid/Grid";
import { NextContentfulImage } from "@components/Image";
import Link from "@components/Link/Link";
import StructuredData from "@components/StructuredData/StructuredData";
import { Author } from "@customTypes/Author";
import { ImageWrapperType } from "@customTypes/imageWrapper";
import { SearchLandingType } from "@customTypes/SearchLandingType";
import { useSettings } from "@utilities/context/settings";
import { getContentfullImagePath } from "@utilities/getContentfullImagePath";
import { useDevice } from "@utilities/react/get-device/get-device";
import RenderComponents from "@utilities/renderComponents";
import clsx from "clsx";
import { format, formatISO, parseISO } from "date-fns";
import uniq from "lodash/uniq";
import Head from "next/head";
import { Fragment } from "react";
import { BSRContentPageProvider } from "../../../context/bsrContentPage";
import styles from "../article.module.scss";
import bsr from "./BSRContentPage.module.scss";

export interface BSRContentPageProps {
  articleTitle: string;
  articleImage?: ImageWrapperType;
  articleThumbnail?: ImageWrapperType;
  articlePublishedDate: string;
  articleDescription?: string;
  articleTimeToRead?: string;
  shortDescription?: string;
  showArticleImage?: boolean;
  bodyContent: any;
  bodyContentBottom: any;
  authorsHeading?: string;
  authorsList?: Author[];
  basePageTemplate: { fields: any };
  tags: Record<string, string>;
}

export const BSRContentPage = (props: { content: BSRContentPageProps }) => {
  const { content } = props;
  const {
    articleDescription,
    articleTimeToRead,
    articleImage,
    articlePublishedDate,
    bodyContent,
    bodyContentBottom,
    articleThumbnail,
    articleTitle,
    shortDescription,
    showArticleImage,
    authorsHeading,
    authorsList,
    tags = {},
  } = content;

  const { siteSettings } = useSettings();

  const device = useDevice();
  const isMobile = device === "mobile";
  const thinkTopicsPage =
    siteSettings?.searchResultsPagesCollection?.items.filter(
      ({ type }) => type === SearchLandingType.ThinkTopics
    )[0];

  const thinkAuthorsPage =
    siteSettings?.searchResultsPagesCollection?.items?.filter(
      ({ type }) => type === SearchLandingType.ThinkAuthors
    )[0];

  function getAuthorLink(author: Author) {
    const facultyPage = author.fields?.articleAuthorFacultyProfile?.fields.slug;
    const contributorPage =
      author.fields?.articleAuthorContributorProfile?.fields.slug;

    if (facultyPage || contributorPage) {
      return `/${facultyPage || contributorPage}`;
    }

    const queryParam = thinkAuthorsPage?.queryParams;
    const slug = thinkAuthorsPage?.url?.slug;
    const nameParam = author.fields.articleAuthorName.replaceAll(" ", "");

    return `/${slug}?${queryParam}=${nameParam}`;
  }

  const formattedDate = formatISO(articlePublishedDate);
  const formattedAuthorList = authorsList.map((author) => {
    return {
      "@type": "Person",
      name: author.fields.articleAuthorName,
      url: `${process.env.NEXT_PUBLIC_URL}${getAuthorLink(author)}`,
    };
  });

  const schemaData = {
    "@context": "http://schema.org",
    "@type": "NewsArticle",
    headline: articleTitle,
    image: `https:${articleImage?.fields?.url}`,
    datePublished: formattedDate,
    dateModified: formattedDate,
    author: formattedAuthorList,
    publisher: {
      "@type": "Organization",
      name: "London Business School",
      logo: {
        "@type": "ImageObject",
        url: "https://images.london.edu/hxo16fanegqh/4KD0tsxILYEZKUprJxltZT/127b23210dfc15b641376e29117796a5/lbs-logo.png",
      },
    },
    description: articleDescription,
  };

  const articleMediaType = uniq(
    Object.values(tags)
      ?.filter((t) => t?.toLowerCase()?.startsWith("media:"))
      ?.map((t) => t?.replaceAll(/^Media:/gim, ""))
  );

  return (
    <div className={bsr.lbsThinkColor}>
      <BSRContentPageProvider details={{ authorsHeading, authorsList }}>
        <Head>
          <meta
            name="articledateyear"
            content={format(parseISO(articlePublishedDate), "yyyy")}
          />
          <meta
            name="articledate"
            content={format(parseISO(articlePublishedDate), "yyyy-MM-dd")}
          />
          <meta
            name="articledatetext"
            content={format(parseISO(articlePublishedDate), "dd MMMM yyyy")}
          />
          <meta name="articlemediatype" content={articleMediaType} />
          <meta name="articletitle" content={articleTitle} />
          <meta name="ShortDescription" content={shortDescription} />
          {!!articleThumbnail && (
            <>
              <meta
                name="articleimagealt"
                content={articleThumbnail?.fields?.altText}
              />
              <meta
                name="articleimageurl"
                content={
                  getContentfullImagePath(`${articleThumbnail?.fields?.url}`) ||
                  ""
                }
              />
              <meta
                key="twitter:image"
                name="twitter:image"
                content={`${articleThumbnail?.fields?.url}?w=1200&h=1200`}
              />
              <meta
                key="og:image"
                property="og:image"
                content={`${articleThumbnail?.fields?.url}?w=1200&h=600`}
              />
            </>
          )}
          {authorsList && (
            <meta
              name="articleauthorsnames"
              content={authorsList
                .map((author) => {
                  return author.fields.articleAuthorName;
                })
                .join(";")}
            />
          )}
        </Head>
        <C200_1_BrandIconTitle isH2={true} />
        <div className="wrapper">
          <Grid row>
            <Grid column sm={12}>
              <section
                className={clsx(
                  "component",
                  styles["article-header"],
                  bsr["article-header"]
                )}
              >
                <div className="wrapper">
                  <div>
                    <h1>{articleTitle}</h1>
                    <p>{articleDescription}</p>
                  </div>
                  <div
                    className={clsx(
                      styles["article-details-wrapper"],
                      bsr["article-details-wrapper"],
                      "flex",
                      "justify-between",
                      "items-start",
                      "gap-4"
                    )}
                  >
                    <div
                      className={clsx(
                        styles["article-details"],
                        bsr["article-details"],
                        "md:w-2/3"
                      )}
                    >
                      {authorsList && (
                        <div className={styles.author}>
                          {authorsList.map((author, index) => {
                            const i = index + 1;
                            const authorshipLink = getAuthorLink(author);
                            const authorName = author.fields.articleAuthorName;
                            return (
                              <Fragment key={authorName}>
                                <Link
                                  key={`author-${authorName}`}
                                  href={authorshipLink}
                                >
                                  {authorName}
                                </Link>
                                {i < authorsList.length - 1 && ", "}
                                {i === authorsList.length - 1 && " and "}
                              </Fragment>
                            );
                          })}
                        </div>
                      )}

                      <div
                        className={clsx(
                          styles["date-read-time"],
                          bsr["date-read-time"]
                        )}
                      >
                        <span className={clsx(styles.date, bsr.date)}>
                          {format(
                            parseISO(articlePublishedDate),
                            "dd MMMM yyyy"
                          )}
                        </span>
                        {articleTimeToRead && (
                          <span className={styles["read-time"]}>
                            {articleTimeToRead}
                          </span>
                        )}
                      </div>
                    </div>
                    {!isMobile && (
                      <div className={styles.share}>
                        <C15_SocialSharing
                          socialLinks={
                            siteSettings?.globalSocialShareCollection
                          }
                          isSocialIconDark
                          small
                          mobileAlignment
                        />
                        <C30_SaveToMyProfile />
                      </div>
                    )}
                  </div>

                  <div
                    className={clsx(
                      "component",
                      "tag",
                      styles.tag,
                      bsr.tag,
                      "md:w-2/3"
                    )}
                  >
                    {Object.keys(tags).map((key) => {
                      const [, tagName] = getTopicIdAndName(tags[key]);
                      return (
                        <Link
                          key={`newsTopic-${tagName}`}
                          href={`/${thinkTopicsPage?.url?.slug}?${thinkTopicsPage?.queryParams}=${tagName}`}
                        >
                          {tagName}
                        </Link>
                      );
                    })}
                  </div>
                  {isMobile && (
                    <div className={styles.share}>
                      <C15_SocialSharing
                        socialLinks={siteSettings?.globalSocialShareCollection}
                        isSocialIconDark
                        small
                        mobileAlignment
                      />
                      <C30_SaveToMyProfile />
                    </div>
                  )}
                </div>
              </section>
              {articleImage && showArticleImage && (
                <section
                  className={clsx(
                    "component",
                    styles["article-images"],
                    "full-width"
                  )}
                >
                  <NextContentfulImage
                    src={articleImage.fields?.url}
                    alt={articleImage.fields?.altText || ""}
                    width={1140}
                    height={346}
                    preload
                    fetchPriority="high"
                  />
                </section>
              )}
            </Grid>
          </Grid>
        </div>
        {RenderComponents(bodyContent)}
        {RenderComponents(bodyContentBottom)}
        <StructuredData id="bsrSiteMeta" schemaData={schemaData} />
      </BSRContentPageProvider>
    </div>
  );
};

export default BSRContentPage;
